import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref } from "vue"

import type { ClassificationInformationSingleLevelBaseDto } from "@api/classification-information-single-level-base-dto";
import { getEntityTranslation } from "@/helpers";
import publicFormApi from "@/publicFormApi";
import type { IMultiLevelSelectOption, ISelectOption } from "@hakosalo/hakolib";
import type { Ref } from "vue";
import { computed, onMounted, ref } from "vue";
import { Field } from "@/types/interfaces/field";

type OptionType = T & {
	id: number | null;
	parentId?: number | null;
	children?: T[];
	enabled: boolean;
	translations: Array<{ languageCode: string }> | null;
};

type OptionTypeNotNull = Omit<OptionType, "id"> & { id: number };

type OptionsType = Array<OptionType>;

interface Props {
	field?: Field;
	valueSingle?: OptionType | null;
	valueSingleId?: number | null;
	valueMulti?: OptionType[];
	infoTable?: string;
	translationsTable?: string;
	translationProperty?: string;
	isInvalid?: boolean | null;
	tree?: boolean;
	ajax?: boolean;
	optionFilter?: (option: OptionType) => boolean;
	noCascade?: boolean;
	disableTopLevelSelection?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ClassificationInformationsSelect2',
  props: {
    field: {},
    valueSingle: {},
    valueSingleId: {},
    valueMulti: {},
    infoTable: {},
    translationsTable: {},
    translationProperty: { default: "name" },
    isInvalid: { type: [Boolean, null], default: null },
    tree: { type: Boolean, default: false },
    ajax: { type: Boolean, default: false },
    optionFilter: {},
    noCascade: { type: Boolean },
    disableTopLevelSelection: { type: Boolean }
  },
  emits: [
	"update:valueSingle",
	"update:valueSingleId",
	"update:valueMulti",
	"blur",
],
  setup(__props: any, { emit: __emit }) {

const emit = __emit;

const props = __props;

const options = ref<OptionsType>([]) as Ref<OptionsType>;

const cValueSingle = computed({
	get() {
		return props.valueSingle as OptionType;
	},
	set(newVal: OptionType) {
		emit("update:valueSingleId", newVal?.id || null);
		emit("update:valueSingle", newVal);
	},
});

const cValueMulti = computed({
	get() {
		return props.valueMulti as OptionType[];
	},
	set(newVal: OptionType[]) {
		emit("update:valueMulti", newVal);
	},
});

const fnValueToOption = (val: OptionType) => {
	if (!val) {
		return null;
	}
	return {
		text: getEntityTranslation(val.translations, props.translationProperty),
		value: val,
	} as ISelectOption<OptionType>;
};

const mapToMultiLevelSelectOption = (options: OptionType[]) => {
	return options.map((item) => {
		const option = {
			key: item.id,
			obj: item,
			value: item.id,
			text: getEntityTranslation(item.translations, props.translationProperty),
			disabled: false,
			children: [] as IMultiLevelSelectOption<OptionType>[],
		} as IMultiLevelSelectOption<OptionType>;

		if (Array.isArray(item.children) && item.children.length > 0) {
			option.children = mapToMultiLevelSelectOption(item.children as OptionType[]);
		}
		return option;
	});
};

const getOptions = (search?: string) => {
	if (props.infoTable && props.translationsTable) {
		publicFormApi.call.form.get
			.getClassificationInformationsSingleLevel2<OptionType>(
				props.infoTable,
				props.translationsTable,
				[props.translationProperty],
				search
			)
			.then((res) => {
				if (res.data) {
					options.value = res.data as unknown as OptionsType;
					if (props.optionFilter) {
						options.value = options.value.filter(props.optionFilter);
					}
				}
			});
	}
};

const loadOptions = async (search?: string, page?: number) => {
	if (props.infoTable && props.translationsTable) {
		const data =
			await publicFormApi.call.form.get.getClassificationInformationsSingleLevel2<OptionType>(
				props.infoTable,
				props.translationsTable,
				[props.translationProperty],
				search
			);
		if (data && data.data) {
			return data.data as unknown as OptionsType[];
		}
	}
	return [] as OptionsType[];
};

onMounted(() => {
	if (!props.ajax) {
		getOptions();
	}
});

return (_ctx: any,_cache: any) => {
  const _component_HakoSingleSelect2 = _resolveComponent("HakoSingleSelect2")!
  const _component_HakoMultiselect2 = _resolveComponent("HakoMultiselect2")!
  const _component_HakoTreeSelect = _resolveComponent("HakoTreeSelect")!

  return (_openBlock(), _createElementBlock("div", null, [
    (cValueSingle.value !== undefined)
      ? (_openBlock(), _createBlock(_component_HakoSingleSelect2, {
          key: 0,
          modelValue: cValueSingle.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((cValueSingle).value = $event)),
          useAjax: _ctx.ajax,
          onBlur: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('blur'))),
          isInvalid: _ctx.isInvalid,
          optionGenerator: fnValueToOption,
          optionComparer: (r) => r.id.toString(),
          optionLoader: loadOptions,
          optionFilter: props.optionFilter,
          useWindowMenu: ""
        }, null, 8, ["modelValue", "useAjax", "isInvalid", "optionComparer", "optionFilter"]))
      : _createCommentVNode("", true),
    (!_ctx.tree)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          (cValueMulti.value !== undefined)
            ? (_openBlock(), _createBlock(_component_HakoMultiselect2, {
                key: 0,
                modelValue: cValueMulti.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((cValueMulti).value = $event)),
                useAjax: _ctx.ajax,
                onBlur: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('blur'))),
                isInvalid: _ctx.isInvalid,
                optionGenerator: fnValueToOption,
                optionComparer: (r) => r.id.toString(),
                optionLoader: loadOptions,
                optionFilter: props.optionFilter,
                useWindowMenu: ""
              }, null, 8, ["modelValue", "useAjax", "isInvalid", "optionComparer", "optionFilter"]))
            : _createCommentVNode("", true)
        ], 64))
      : (_openBlock(), _createBlock(_component_HakoTreeSelect, {
          key: 2,
          modelValue: cValueMulti.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((cValueMulti).value = $event)),
          options: options.value,
          "option-generator": 
					(o) => ({
						key: o.id!.toString(),
						parentKey: o.parentId == null ? null : o.parentId!.toString(),
						hasChildren: Array.isArray(o.children) && o.children!.length > 0,
						text: _unref(getEntityTranslation)(o.translations, props.translationProperty) || '',
						isHeader:
							(_ctx.disableTopLevelSelection || false) == true && o.parentId == null
								? true
								: false,
					})
				,
          noCascade: _ctx.noCascade || undefined
        }, null, 8, ["modelValue", "options", "option-generator", "noCascade"]))
  ]))
}
}

})